/* Two shareable gallery views, one on-page player. No third-party load before a click. */
(() => {
  'use strict';
  if (window.etMediaLifecycleBound) return;
  window.etMediaLifecycleBound = true;
  const mounted = new Map();
  const externalPlayer = (raw) => {
    let url;
    try { url = new URL(raw, location.href); } catch (_) { return null; }
    if (!['http:', 'https:'].includes(url.protocol) || url.username || url.password || url.port) return null;
    const host = url.hostname.toLowerCase();
    const parts = url.pathname.split('/').filter(Boolean);
    let id = '';
    if (host === 'youtu.be' || host === 'www.youtu.be') id = parts[0] || '';
    else if (['youtube.com', 'www.youtube.com', 'm.youtube.com', 'youtube-nocookie.com', 'www.youtube-nocookie.com'].includes(host)) {
      if (parts[0] === 'watch') id = url.searchParams.get('v') || '';
      else if (['shorts', 'embed', 'live'].includes(parts[0])) id = parts[1] || '';
    }
    if (/^[A-Za-z0-9_-]{11}$/.test(id)) return { src: `https://www.youtube-nocookie.com/embed/${encodeURIComponent(id)}?autoplay=1&playsinline=1&rel=0`, provider: 'youtube' };
    if (['vimeo.com', 'www.vimeo.com', 'player.vimeo.com'].includes(host)) {
      if (host === 'player.vimeo.com' && parts[0] === 'video') id = parts[1] || '';
      else if (/^\d+$/.test(parts[0] || '')) id = parts[0];
      else if (parts[0] === 'channels' && parts.length >= 3) id = parts[2];
      else if (parts[0] === 'groups' && parts[2] === 'videos') id = parts[3] || '';
      if (!/^\d{5,12}$/.test(id)) return null;
      const privateHash = url.searchParams.get('h') || (/^\d+$/.test(parts[0] || '') ? parts[1] : '') || '';
      let query = '?autoplay=1&dnt=1';
      if (/^[A-Za-z0-9]+$/.test(privateHash)) query += `&h=${encodeURIComponent(privateHash)}`;
      return { src: `https://player.vimeo.com/video/${id}${query}`, provider: 'vimeo' };
    }
    if (['instagram.com', 'www.instagram.com', 'm.instagram.com'].includes(host)) {
      if (!['reel', 'reels', 'p', 'tv'].includes(parts[0]) || !/^[A-Za-z0-9_-]{6,32}$/.test(parts[1] || '')) return null;
      return { src: `https://www.instagram.com/p/${encodeURIComponent(parts[1])}/embed/`, provider: 'instagram' };
    }
    if (url.protocol === 'https:' && /\.(mp4|webm|ogv|ogg|m4v)$/i.test(url.pathname)) return { src: url.href, provider: 'video', native: true };
    return null;
  };
  const initialise = (root) => {
    if (mounted.has(root)) return;
    const listeners = [];
    const on = (target, event, callback) => { if (!target) return; target.addEventListener(event, callback); listeners.push(() => target.removeEventListener(event, callback)); };
    const tabs = Array.from(root.querySelectorAll('[data-media-filter]'));
    const panels = Array.from(root.querySelectorAll('[data-media-panel]'));
    const dialog = root.querySelector('[data-media-dialog]');
    const stage = root.querySelector('[data-media-stage]');
    const title = root.querySelector('[data-media-dialog-title]');
    const photos = Array.from(root.querySelectorAll('[data-media-photo]'));
    const controls = root.querySelector('[data-media-gallery-controls]');
    const credit = root.querySelector('[data-media-dialog-credit]');
    const close = root.querySelector('[data-media-close]');
    let opener = null;
    let currentPhoto = -1;
    let observer;
    let savedOverflow = null;
    const hashKey = () => /^(#videos|#films|#MediaFilms-)/.test(location.hash) ? 'films' : 'photos';
    const activate = (key, moveFocus = false, updateURL = false) => {
      if (!['photos', 'films'].includes(key)) return;
      tabs.forEach((tab) => {
        const selected = tab.dataset.mediaFilter === key;
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
        if (selected && moveFocus) tab.focus();
      });
      panels.forEach((panel) => { panel.hidden = panel.dataset.mediaPanel !== key; });
      if (updateURL) {
        try { const url = new URL(location.href); url.hash = key === 'films' ? 'videos' : 'images'; history.replaceState(history.state, '', url); } catch (_) { /* Tabs still work if the editor restricts History. */ }
      }
      if (window.ScrollTrigger) window.ScrollTrigger.refresh();
    };
    if (tabs.length && panels.length) {
      tabs.forEach((tab, index) => {
        const panel = panels.find((item) => item.dataset.mediaPanel === tab.dataset.mediaFilter);
        if (panel) { panel.setAttribute('role', 'tabpanel'); panel.setAttribute('aria-labelledby', tab.id); }
        on(tab, 'click', () => activate(tab.dataset.mediaFilter, false, true));
        on(tab, 'keydown', (event) => {
          let next = index;
          if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
          else if (event.key === 'ArrowLeft') next = (index + tabs.length - 1) % tabs.length;
          else if (event.key === 'Home') next = 0;
          else if (event.key === 'End') next = tabs.length - 1;
          else return;
          event.preventDefault(); activate(tabs[next].dataset.mediaFilter, true, true);
        });
      });
      root.querySelectorAll('[data-media-switch]').forEach((button) => on(button, 'click', (event) => { event.preventDefault(); activate(button.dataset.mediaSwitch, true, true); }));
      activate(hashKey());
      on(window, 'hashchange', () => activate(hashKey()));
      root.querySelector('[role="tablist"]').hidden = false;
      root.classList.add('is-enhanced');
    }
    if (document.body.dataset.motion === 'true' && !matchMedia('(prefers-reduced-motion: reduce)').matches && 'IntersectionObserver' in window && typeof Element.prototype.animate === 'function') {
      observer = new IntersectionObserver((entries) => entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        observer.unobserve(entry.target);
        entry.target.animate([{ opacity: .65, transform: 'translateY(18px)' }, { opacity: 1, transform: 'translateY(0)' }], { duration: 700, easing: 'cubic-bezier(.2,.7,.3,1)' });
      }), { threshold: .08 });
      root.querySelectorAll('.et-media__frame, .et-media__film, .et-media__interlude').forEach((frame) => observer.observe(frame));
    }
    const clearStage = () => {
      if (!stage) return;
      stage.querySelectorAll('video').forEach((video) => { try { video.pause(); video.removeAttribute('src'); video.querySelectorAll('source').forEach((source) => source.removeAttribute('src')); video.load(); } catch (_) { /* Element removal also ends playback. */ } });
      stage.querySelectorAll('iframe').forEach((frame) => frame.removeAttribute('src'));
      stage.replaceChildren();
      if (credit) { credit.replaceChildren(); credit.hidden = true; }
    };
    const loadCredit = (link, film = false) => {
      if (!credit) return;
      const owner = link.closest('.et-media__film, .et-media__frame');
      const source = owner && owner.querySelector('.et-media__credit');
      if (source) credit.append(source.cloneNode(true));
      if (film && link.dataset.provider !== 'hosted') {
        const original = document.createElement('a');
        original.href = link.href;
        original.target = '_blank';
        original.rel = 'noopener noreferrer';
        original.textContent = `${root.dataset.originalLabel || 'Open original video'} ↗`;
        credit.append(original);
      }
      credit.hidden = !credit.childElementCount;
    };
    if (dialog && stage && typeof dialog.showModal === 'function') {
      const show = (link) => {
        opener = link;
        if (!dialog.open) { savedOverflow = document.documentElement.style.overflow; document.documentElement.style.overflow = 'hidden'; dialog.showModal(); }
        close.focus();
      };
      const loadPhoto = (index) => {
        if (!photos.length) return;
        clearStage();
        currentPhoto = (index + photos.length) % photos.length;
        const link = photos[currentPhoto];
        const img = document.createElement('img');
        img.src = link.href; img.alt = link.dataset.alt || link.dataset.caption || '';
        title.textContent = link.dataset.caption || '';
        dialog.dataset.format = 'photo';
        stage.append(img); loadCredit(link);
        controls.hidden = photos.length < 2;
        root.querySelector('[data-media-position]').textContent = `${currentPhoto + 1} / ${photos.length}`;
      };
      photos.forEach((link, index) => on(link, 'click', (event) => {
        if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
        event.preventDefault(); loadPhoto(index); show(link);
      }));
      root.querySelectorAll('[data-media-film]').forEach((link) => on(link, 'click', (event) => {
        if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        let player;
        const provider = link.dataset.provider;
        const descriptor = externalPlayer(link.href);
        if (provider === 'hosted') {
          const template = link.parentElement.querySelector('[data-media-video]');
          const source = template && template.content.querySelector('video');
          if (source) { player = source.cloneNode(true); player.autoplay = true; player.controls = true; player.playsInline = true; }
        } else if (descriptor && descriptor.native) {
          player = document.createElement('video'); player.src = descriptor.src; player.controls = true; player.playsInline = true; player.autoplay = true; player.preload = 'metadata';
        } else if (descriptor) {
          player = document.createElement('iframe'); player.src = descriptor.src; player.dataset.provider = descriptor.provider;
          player.title = link.dataset.caption || 'Film player'; player.allow = 'autoplay; fullscreen; picture-in-picture; encrypted-media'; player.referrerPolicy = 'strict-origin-when-cross-origin'; player.allowFullscreen = true;
        }
        clearStage(); currentPhoto = -1; controls.hidden = true;
        title.textContent = link.dataset.caption || '';
        dialog.dataset.format = link.dataset.format || 'landscape';
        if (player) stage.append(player);
        else { const message = document.createElement('p'); message.className = 'et-media__player-notice'; message.textContent = 'This video cannot be played here yet. You can open the original video below.'; message.setAttribute('role', 'status'); stage.append(message); }
        loadCredit(link, true); show(link);
        if (player && player.tagName === 'VIDEO') {
          const playback = player.play();
          if (playback && typeof playback.catch === 'function') playback.catch(() => { /* Native controls remain available if autoplay is blocked. */ });
        }
      }));
      on(close, 'click', () => dialog.close());
      on(root.querySelector('[data-media-prev]'), 'click', () => loadPhoto(currentPhoto - 1));
      on(root.querySelector('[data-media-next]'), 'click', () => loadPhoto(currentPhoto + 1));
      on(dialog, 'close', () => { clearStage(); if (savedOverflow !== null) { document.documentElement.style.overflow = savedOverflow; savedOverflow = null; } if (opener && opener.isConnected) opener.focus({ preventScroll: true }); });
      on(dialog, 'keydown', (event) => { if (currentPhoto < 0 || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return; event.preventDefault(); loadPhoto(currentPhoto + (event.key === 'ArrowRight' ? 1 : -1)); });
    }
    on(root, 'shopify:block:select', (event) => { const block = event.target.closest('.et-media__film, .et-media__frame'); if (block) activate(block.classList.contains('et-media__film') ? 'films' : 'photos'); });
    mounted.set(root, () => { if (observer) observer.disconnect(); if (dialog && dialog.open) dialog.close(); if (savedOverflow !== null) document.documentElement.style.overflow = savedOverflow; clearStage(); listeners.forEach((dispose) => dispose()); mounted.delete(root); });
  };
  const scan = (scope) => { if (scope.matches && scope.matches('[data-et-media]')) initialise(scope); scope.querySelectorAll('[data-et-media]').forEach(initialise); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan(document), { once: true }); else scan(document);
  document.addEventListener('shopify:section:load', (event) => scan(event.target));
  document.addEventListener('shopify:section:unload', (event) => { for (const [root, dispose] of mounted) if (event.target === root || event.target.contains(root)) dispose(); });
})();
